// Pure build-identity and update-boundary rules; the store wires them to the router and DOM.

export interface BuildInfo {
  version: string;
  builtAt: string;
}

/** remote: loaded from the CDN, can update. embedded: bundled in the APK, a reload cannot change it. */
export type UpdateMode = 'remote' | 'embedded' | 'dev';

/** Moments the teacher is between tasks - the only times an update is looked for. */
export type UpdateBoundary = 'foreground' | 'home' | 'lessonEnd';

export function parseBuildInfo(raw: unknown): BuildInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.version !== 'string' || !value.version) return null;
  return { version: value.version, builtAt: typeof value.builtAt === 'string' ? value.builtAt : '' };
}

/** Any difference counts: a rollback is as much "what is deployed now" as a newer build. */
export function hasUpdate(current: BuildInfo, remote: BuildInfo | null): boolean {
  return remote !== null && remote.version !== current.version;
}

export function updateMode(dev: boolean, location: { hostname: string; protocol: string }): UpdateMode {
  if (dev) return 'dev';
  // the embedded APK serves from tauri.localhost; the remote APK carries the CDN origin
  if (/(^|\.)tauri\.localhost$/i.test(location.hostname) || location.protocol === 'tauri:') return 'embedded';
  return 'remote';
}

export function isBoardPath(pathname: string): boolean {
  return /^\/board(\/|$)/.test(pathname);
}

/** Arriving on home is a boundary; arriving from a board means the lesson just ended. */
export function navigationBoundary(previous: string, next: string): UpdateBoundary | null {
  if (next !== '/' || previous === '/') return null;
  return isBoardPath(previous) ? 'lessonEnd' : 'home';
}

export interface PromptInput {
  boundary: UpdateBoundary;
  pathname: string;
  current: BuildInfo;
  remote: BuildInfo | null;
  dismissed: string | null;
  dialogOpen: boolean;
}

/** Home only, never over another dialog; "Để sau" holds a build back until the next lesson ends. */
export function shouldPrompt(input: PromptInput): boolean {
  if (input.pathname !== '/' || input.dialogOpen) return false;
  if (!hasUpdate(input.current, input.remote)) return false;
  return input.boundary === 'lessonEnd' || input.dismissed !== (input.remote as BuildInfo).version;
}

/** "14:30 09/10/2026" in Vietnam time, whatever zone the panel is set to. */
export function formatBuildTime(builtAt: string): string {
  const time = Date.parse(builtAt);
  if (isNaN(time)) return '';
  const iso = new Date(time + 7 * 60 * 60 * 1000).toISOString();
  return `${iso.slice(11, 16)} ${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}
